const IGNORED_FIELDS = [
  'updated_at',
  'created_at',
  'account_id',
  'custom_attributes',
  'last_activity_at',
];

// Sub-campos do jsonb additional_attributes que viram mensagem propria no
// Historico (bio, redes sociais etc.) - o resto do hash (ex.: campos
// internos de integracao) continua sem gerar mensagem. country_code fica
// de fora de proposito: o Contact ja tem uma coluna de topo com o mesmo
// nome (sincronizada por Contacts::SyncAttributes a partir do pais por
// extenso) que gera essa mensagem sozinha - rastrear os dois duplicava.
const ADDITIONAL_ATTRIBUTES_FIELDS = ['description', 'city', 'company_name'];
const SOCIAL_PROFILE_FIELDS = [
  'facebook',
  'github',
  'instagram',
  'telegram',
  'tiktok',
  'linkedin',
  'twitter',
];

// Colunas espelho: mesmo dado gravado em dois lugares. Sozinha (sem o campo
// de origem no mesmo evento) usa o rotulo do campo de origem.
const FIELD_LABEL_ALIASES = { location: 'city' };

const fieldLabel = (field, t, te) => {
  const key = `TIMELINE.FIELDS.${FIELD_LABEL_ALIASES[field] || field}`;
  return te(key) ? t(key) : field.replace(/_/g, ' ');
};

const flattenAdditionalAttributes = attrs => {
  const flat = {};
  ADDITIONAL_ATTRIBUTES_FIELDS.forEach(field => {
    flat[field] = attrs?.[field];
  });
  SOCIAL_PROFILE_FIELDS.forEach(field => {
    flat[`social_profiles.${field}`] = attrs?.social_profiles?.[field];
  });
  return flat;
};

const additionalAttributesChanges = (from, to) => {
  const flatFrom = flattenAdditionalAttributes(from);
  const flatTo = flattenAdditionalAttributes(to);
  return Object.keys(flatTo).filter(
    field => (flatFrom[field] || '') !== (flatTo[field] || '')
  );
};

// Mudancas que o sistema grava SOZINHAS junto com a edicao de outro campo -
// o usuario mexeu so no campo de origem, entao nao viram mensagem propria:
// - location: Contacts::SyncAttributes copia additional_attributes.city
// - contact_type: o mesmo servico promove visitante -> lead ao ganhar
//   e-mail, telefone ou rede social
const DERIVED_FIELD_RULES = {
  location: event => {
    const [from, to] = event.changes.additional_attributes || [];
    return additionalAttributesChanges(from, to).includes('city');
  },
  contact_type: event => {
    const [from, to] = event.changes.contact_type;
    const sources = ['email', 'phone_number', 'additional_attributes'];
    return (
      from === 'visitor' &&
      to === 'lead' &&
      sources.some(source => source in event.changes)
    );
  },
};

const isDerivedChange = (field, event) =>
  DERIVED_FIELD_RULES[field]?.(event) ?? false;

export const getChangedFields = event =>
  Object.keys(event.changes || {}).filter(
    field => !IGNORED_FIELDS.includes(field)
  );

const isBlank = value => value === null || value === undefined || value === '';

const identityResolver = value => value;

// Aplica o resolver so em valores reais - nulo/vazio sempre vira "—",
// sem depender do resolver saber lidar com esse caso.
const resolveWithFallback = (value, resolver) =>
  value === null || value === undefined || value === '' ? '—' : resolver(value);

// Monta as mensagens legiveis de um evento de auditoria da timeline
// ("Fulano alterou campo de X para Y, data"), reaproveitado tanto no
// painel lateral (TimelineItem.vue) quanto no historico em chat das
// paginas de Contato/Empresa/Negociacao (TimelineChat.vue).
export function buildTimelineEventMessages(
  event,
  {
    t,
    te,
    locale,
    resolveStageName = identityResolver,
    resolveCompanyName = identityResolver,
    resolveContactName = identityResolver,
  }
) {
  const actor = event.user_name || t('TIMELINE.SYSTEM_ACTOR');
  const formattedDate = new Date(event.created_at).toLocaleString(
    locale?.replace('_', '-')
  );

  const FIELD_RESOLVERS = {
    company_id: resolveCompanyName,
    contact_id: resolveContactName,
  };

  if (event.action === 'create') {
    const entity = t(
      `TIMELINE.ENTITY_LABEL.${event.subject_type.toUpperCase()}`
    );
    return [t('TIMELINE.CREATED_BY', { actor, entity, date: formattedDate })];
  }

  if (
    event.action === 'deal_created_for_contact' ||
    event.action === 'deal_deleted_for_contact'
  ) {
    const key =
      event.action === 'deal_created_for_contact'
        ? 'TIMELINE.DEAL_CREATED_FOR_CONTACT'
        : 'TIMELINE.DEAL_DELETED_FOR_CONTACT';
    return [
      t(key, {
        actor,
        name: event.changes.name || '—',
        date: formattedDate,
      }),
    ];
  }

  if (event.action === 'label_added' || event.action === 'label_removed') {
    const key =
      event.action === 'label_added'
        ? 'TIMELINE.LABEL_ADDED'
        : 'TIMELINE.LABEL_REMOVED';
    return [
      t(key, {
        actor,
        label: event.changes.label_title,
        date: formattedDate,
      }),
    ];
  }

  return getChangedFields(event).flatMap(field => {
    if (isDerivedChange(field, event)) return [];

    const change = event.changes[field];
    const [from, to] = Array.isArray(change) ? change : [null, change];

    if (field === 'pipeline_stage_id') {
      return t('TIMELINE.STAGE_CHANGED_DETAIL', {
        actor,
        from: resolveWithFallback(from, resolveStageName),
        to: resolveWithFallback(to, resolveStageName),
        date: formattedDate,
      });
    }

    if (field === 'additional_attributes') {
      const [attrsFrom, attrsTo] = Array.isArray(change)
        ? change
        : [{}, change];
      const flatFrom = flattenAdditionalAttributes(attrsFrom);
      const flatTo = flattenAdditionalAttributes(attrsTo);

      return additionalAttributesChanges(attrsFrom, attrsTo).map(subField =>
        t('TIMELINE.FIELD_CHANGED', {
          actor,
          field: fieldLabel(subField, t, te),
          from: resolveWithFallback(flatFrom[subField], identityResolver),
          to: resolveWithFallback(flatTo[subField], identityResolver),
          date: formattedDate,
        })
      );
    }

    if (typeof to === 'object') {
      return t('TIMELINE.FIELD_UPDATED_GENERIC', {
        actor,
        field: fieldLabel(field, t, te),
        date: formattedDate,
      });
    }

    // vazio -> vazio (ex.: null para "") nao e mudanca: o salvar do formulario
    // reenvia campos em branco e o banco grava como alterados.
    if (isBlank(from) && isBlank(to)) return [];

    const resolve = FIELD_RESOLVERS[field] || identityResolver;

    return t('TIMELINE.FIELD_CHANGED', {
      actor,
      field: fieldLabel(field, t, te),
      from: resolveWithFallback(from, resolve),
      to: resolveWithFallback(to, resolve),
      date: formattedDate,
    });
  });
}
